import { useEffect, useState, useCallback } from "react";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Lightweight in-memory cache + event bus so cards share state
const _cache = { product: new Set(), request: new Set(), loaded: false };
const _listeners = new Set();

function _notify() {
  _listeners.forEach((fn) => fn());
}

export async function hydrateSaves() {
  try {
    const res = await api.get("/saves");
    _cache.product = new Set(res.data?.saved_ids?.product || []);
    _cache.request = new Set(res.data?.saved_ids?.request || []);
    _cache.loaded = true;
    _notify();
  } catch {
    _cache.loaded = true;
  }
}

export function clearSaves() {
  _cache.product = new Set();
  _cache.request = new Set();
  _cache.loaded = false;
  _notify();
}

export const SaveButton = ({ targetType, targetId, variant = "icon", stopPropagation = true, className = "" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, forceRender] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const fn = () => forceRender((n) => n + 1);
    _listeners.add(fn);
    if (user && !_cache.loaded) hydrateSaves();
    return () => _listeners.delete(fn);
  }, [user]);

  const saved = _cache[targetType]?.has(targetId);

  const toggle = useCallback(
    async (e) => {
      if (stopPropagation) {
        e.stopPropagation();
        e.preventDefault();
      }
      if (!user) {
        navigate("/login");
        return;
      }
      setBusy(true);
      try {
        if (saved) {
          await api.delete(`/saves/${targetType}/${targetId}`);
          _cache[targetType].delete(targetId);
          toast.success("Removed from saved");
        } else {
          await api.post(`/saves`, { target_type: targetType, target_id: targetId });
          _cache[targetType].add(targetId);
          toast.success("Saved");
        }
        _notify();
      } catch {
        toast.error("Could not update saved");
      } finally {
        setBusy(false);
      }
    },
    [user, saved, targetId, targetType, stopPropagation, navigate]
  );

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={`save-btn-${targetType}-${targetId}`}
        className={`inline-flex items-center gap-1.5 rounded-full border px-4 h-10 text-xs font-semibold transition-colors ${
          saved
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-white border-border hover:border-foreground/30"
        } ${className}`}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : saved ? (
          <BookmarkCheck className="h-3.5 w-3.5" fill="currentColor" strokeWidth={2} />
        ) : (
          <Bookmark className="h-3.5 w-3.5" strokeWidth={2} />
        )}
        {saved ? "Saved" : "Save"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`save-btn-${targetType}-${targetId}`}
      aria-label={saved ? "Unsave" : "Save"}
      className={`inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/95 backdrop-blur-sm border border-border shadow-sm hover:border-foreground/30 transition-colors ${className}`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : saved ? (
        <BookmarkCheck className="h-4 w-4 text-primary" fill="currentColor" strokeWidth={2} />
      ) : (
        <Bookmark className="h-4 w-4" strokeWidth={2} />
      )}
    </button>
  );
};
